import { economyConfig } from '../config/economyConfig';
import { BUILDINGS } from '../data/buildings';
import { getDistrict } from '../data/districts';
import { getUpgrade } from '../data/upgrades';
import type { GameState } from '../models/GameState';
import type { ParkingLot } from '../models/ParkingLot';
import { TILE, type TileType } from '../models/Tile';
import { roundMoney } from '../utils/math';
import { MarketEngine } from './MarketEngine';

export interface ValuationBreakdown {
  cash: number;
  assets: number; // land + construction + upgrades
  profit: number; // capitalized recent monthly profit
  reputation: number;
  marketShare: number;
  total: number;
  marketSharePct: number; // 0..1, for the objective check
}

// tile -> purchase cost, for asset valuation.
const TILE_COST = new Map<TileType, number>();
for (const def of Object.values(BUILDINGS)) TILE_COST.set(def.tile, def.purchaseCost);

/**
 * ValuationEngine (spec section 38): estimates company value from cash, built
 * assets, capitalized recent profit, reputation and market share — and checks
 * the campaign win condition. Pure & config-driven.
 */
export const ValuationEngine = {
  lotAssetValue(lot: ParkingLot): number {
    const land = getDistrict(lot.districtId)?.landPrice ?? 0;
    let construction = 0;
    for (const tile of lot.tiles) {
      if (tile !== TILE.Empty) construction += TILE_COST.get(tile) ?? 0;
    }
    let tech = 0;
    for (const id of lot.upgrades) tech += getUpgrade(id)?.cost ?? 0;
    return land + construction + tech;
  },

  /** Sum of the player's settled profit over the retained history (~1 month). */
  recentMonthlyProfit(state: GameState): number {
    return state.finance.history.reduce((sum, r) => sum + r.profit, 0);
  },

  compute(state: GameState): ValuationBreakdown {
    const cash = state.player.cash;

    let assets = 0;
    for (const lot of state.parkingLots) assets += this.lotAssetValue(lot);

    const monthlyProfit = this.recentMonthlyProfit(state);
    const profit = Math.max(0, monthlyProfit) * economyConfig.VALUATION.monthlyProfitMultiple;

    const reputation = state.player.reputation * economyConfig.VALUATION.reputationWeight;

    const shares = MarketEngine.overallShares(state, state.time);
    const marketSharePct = shares.find((s) => s.ownerId === 'player')?.share ?? 0;
    const marketShare = marketSharePct * economyConfig.VALUATION.marketShareWeight;

    const total = roundMoney(cash + assets + profit + reputation + marketShare);

    return {
      cash: roundMoney(cash),
      assets: roundMoney(assets),
      profit: roundMoney(profit),
      reputation: roundMoney(reputation),
      marketShare: roundMoney(marketShare),
      total,
      marketSharePct,
    };
  },

  /** True if either win goal is met (spec section 38). */
  isObjectiveMet(v: ValuationBreakdown): boolean {
    return (
      v.total >= economyConfig.GOAL_COMPANY_VALUE ||
      v.marketSharePct >= economyConfig.GOAL_MARKET_SHARE
    );
  },
};
